import React, { useState } from 'react';
import { Box, Avatar, Menu, MenuItem, Typography, Divider, ListItemIcon } from '@mui/material';
import { Logout } from '@mui/icons-material';
import { PALETTE, BORDERS, TINTS, alpha } from '../theme';
import { useAuth } from './AuthContext';

const initialOf = (account) => {
    const src = account?.displayName || account?.email || '';
    return src.trim().charAt(0).toUpperCase() || '?';
};

/**
 * Account control for the top nav.
 *   - guest  → a quiet "Log in" pill that opens the global auth dialog
 *   - signed in → an avatar that opens a menu (email + Log out)
 *
 * My Maps lives in the nav links, not here, so this stays focused on identity.
 */
const AccountMenu = () => {
    const { isSignedIn, account, openAuthDialog, logout } = useAuth();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    if (!isSignedIn) {
        return (
            <Box
                component="button"
                type="button"
                onClick={openAuthDialog}
                sx={{
                    px: 1.75,
                    py: 0.75,
                    borderRadius: 1.5,
                    border: BORDERS.default,
                    backgroundColor: 'transparent',
                    color: PALETTE.text,
                    fontFamily: 'inherit',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease, border-color 0.15s ease',
                    '&:hover': { backgroundColor: TINTS.primary, borderColor: alpha('primary', 0.4) },
                }}
            >
                Log in
            </Box>
        );
    }

    return (
        <>
            <Box
                component="button"
                type="button"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                aria-label="Account menu"
                sx={{
                    p: 0,
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    borderRadius: '50%',
                    lineHeight: 0,
                }}
            >
                <Avatar
                    src={account.photoURL || undefined}
                    sx={{
                        width: 34,
                        height: 34,
                        bgcolor: PALETTE.primary,
                        color: '#fff',
                        fontSize: '0.95rem',
                        fontWeight: 700,
                    }}
                >
                    {initialOf(account)}
                </Avatar>
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { mt: 1, minWidth: 220, borderRadius: 2, border: BORDERS.soft } }}
            >
                <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="caption" sx={{ color: PALETTE.textMuted }}>
                        Signed in as
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: PALETTE.text, wordBreak: 'break-all', lineHeight: 1.3 }}
                    >
                        {account.email}
                    </Typography>
                </Box>
                <Divider />
                <MenuItem
                    onClick={async () => {
                        setAnchorEl(null);
                        await logout();
                    }}
                    sx={{ color: PALETTE.textBody }}
                >
                    <ListItemIcon>
                        <Logout fontSize="small" sx={{ color: PALETTE.textMuted }} />
                    </ListItemIcon>
                    Log out
                </MenuItem>
            </Menu>
        </>
    );
};

export default AccountMenu;
